import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AdminLayout from "../../../layouts/AdminLayout";
import Loader from "../../../components/loader";
import {
  fetchCountriesCurrency,
  fetchAdminSubscriptions,
  updateAdminSubscription,
} from "../../../redux/features/admin/actions/authAction";
import { curSym } from "../../../utils/pip";

const PLAN_FILTERS = ["All", "NuGlow", "NuForge", "NuStar"];
const DURATION_FILTERS = ["All", "30", "90", "180", "365"];

const getExperienceBucket = (value) => {
  const normalized = String(value ?? "").trim();

  if (!normalized) return "N/A";
  if (normalized.includes("+")) return "NuStar";

  const numericValue = Number(normalized);
  if (!Number.isFinite(numericValue)) return "N/A";
  if (numericValue >= 0 && numericValue <= 2) return "NuGlow";
  if (numericValue >= 3 && numericValue <= 5) return "NuForge";
  if (numericValue > 5) return "NuStar";
  return "N/A";
};

const getBucketTextClass = (bucket) => {
  if (bucket === "NuGlow") return "ct_yellow_text";
  if (bucket === "NuForge") return "ct_orange_text";
  if (bucket === "NuStar") return "ct_darksky_blue_text";
  return "ct_grey_text";
};

const getTitleButtonClass = (item) => {
  const title = String(item?.title || "").trim().toLowerCase();
  const price = Number(item?.price ?? 0);

  if (price === 0) return "ct_yellow_btn ct_darksky_btn";
  if (["creator pro", "leader prime", "builder elite"].includes(title)) {
    return "ct_yellow_btn ct_orange_btn";
  }
  return "ct_yellow_btn";
};

const getPlanAccentColor = (item) => {
  const title = String(item?.title || "").trim().toLowerCase();
  const price = Number(item?.price ?? 0);

  if (price === 0) return "#8ecae6";
  if (["creator pro", "leader prime", "builder elite"].includes(title)) {
    return "#fb8500";
  }
  return "#ffb703";
};

const formatDate = (value) => {
  if (!value) return "--";

  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatPrice = (value) => {
  const price = Number(value ?? 0);
  return price === 0 ? "Free" : `${curSym}${price}`;
};

const getDurationLabel = (days) => {
  const duration = Number(days);
  if (duration === 30) return "1 Month";
  if (duration === 90) return "3 Months";
  if (duration === 180) return "6 Months";
  if (duration === 365) return "12 Months";
  return `${days} Days`;
};

const getSubscriptionTypeLabel = (value) => {
  if (String(value || "").trim().toUpperCase() === "OPEN_TALENT") {
    return "Freelancer";
  }

  return value || "--";
};

const getCountryDisplayLabel = (countryId, fallbackLabel, countries) => {
  const matched = (countries || []).find(
    (item) => Number(item?.country_id) === Number(countryId)
  );
  const finalLabel = matched?.label || fallbackLabel || "";

  if (!finalLabel) return "";

  return String(finalLabel).replace(/\s*\([^)]*\)\s*$/, "").trim();
};

const getMergedCountryPrices = (subscription, countries) => {
  const existingPrices = Array.isArray(subscription?.subscription_prices)
    ? subscription.subscription_prices
    : [];

  return (countries || []).map((country) => {
    const matched = existingPrices.find(
      (item) => Number(item?.country_id) === Number(country?.country_id)
    );

    return {
      country_id: country?.country_id,
      label: country?.label || "",
      value: country?.value || "",
      is_free: Number(matched?.price ?? "") === 0,
      price: matched?.price ?? "",
    };
  });
};

const getInitialEditState = (subscription, countries) => ({
  subscription_id: subscription?.subscription_id,
  title: subscription?.title || "",
  description: subscription?.description || "",
  price: subscription?.price ?? 0,
  is_free: Number(subscription?.price ?? 0) === 0,
  subscription_days: subscription?.subscription_days ?? 30,
  subscription_type: subscription?.subscription_type || "OPEN_TALENT",
  years_of_experience: subscription?.years_of_experience || "",
  save_percent: subscription?.save_percent || "",
  subscription_prices: getMergedCountryPrices(subscription, countries),
  details:
    subscription?.details?.length > 0
      ? subscription.details.map((detail) => ({
        feature_name: detail?.feature_name || "",
      }))
      : [{ feature_name: "" }],
});

const Subscription = () => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [planFilter, setPlanFilter] = useState("All");
  const [durationFilter, setDurationFilter] = useState("All");
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const dispatch = useDispatch();
  const { isLoading, adminSubscriptionList, adminCountryCurrencyList } = useSelector(
    (state) => state.admin.auth
  );

  const loadSubscriptions = () => {
    setError("");
    dispatch(fetchAdminSubscriptions()).then((res) => {
      const payload = res?.payload;
      if (!payload) {
        setError("Unable to load subscriptions right now. Please try again.");
      }
    });
  };

  useEffect(() => {
    loadSubscriptions();
    dispatch(fetchCountriesCurrency());
  }, [dispatch]);

  useEffect(() => {
    if (editingSubscription && adminCountryCurrencyList?.length) {
      setEditForm((prev) =>
        prev
          ? {
            ...prev,
            subscription_prices: getMergedCountryPrices(
              editingSubscription,
              adminCountryCurrencyList
            ),
          }
          : prev
      );
    }
  }, [adminCountryCurrencyList, editingSubscription]);

  const visibleSubscriptions = useMemo(
    () =>
      (adminSubscriptionList || []).filter(
        (item) => item?.subscription_type === "OPEN_TALENT"
      ),
    [adminSubscriptionList]
  );

  const filteredSubscriptions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return visibleSubscriptions.filter((item) => {
      const bucket = getExperienceBucket(item?.years_of_experience);
      const detailsText = (item?.details || [])
        .map((detail) => detail?.feature_name || "")
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        !query ||
        String(item?.title || "").toLowerCase().includes(query) ||
        String(item?.description || "").toLowerCase().includes(query) ||
        String(item?.subscription_type || "").toLowerCase().includes(query) ||
        String(item?.years_of_experience || "").toLowerCase().includes(query) ||
        detailsText.includes(query);

      const matchesPlan = planFilter === "All" || bucket === planFilter;
      const matchesDuration =
        durationFilter === "All" ||
        String(item?.subscription_days || "") === durationFilter;

      return matchesSearch && matchesPlan && matchesDuration;
    });
  }, [durationFilter, planFilter, searchTerm, visibleSubscriptions]);

  const summaryCards = useMemo(
    () => [
      {
        label: "All Plans",
        value: visibleSubscriptions.length,
        textClass: "ct_darkblue_text",
      },
      {
        label: "NuGlow",
        value: visibleSubscriptions.filter(
          (item) => getExperienceBucket(item?.years_of_experience) === "NuGlow"
        ).length,
        textClass: "ct_yellow_text",
      },
      {
        label: "NuForge",
        value: visibleSubscriptions.filter(
          (item) => getExperienceBucket(item?.years_of_experience) === "NuForge"
        ).length,
        textClass: "ct_orange_text",
      },
      {
        label: "NuStar",
        value: visibleSubscriptions.filter(
          (item) => getExperienceBucket(item?.years_of_experience) === "NuStar"
        ).length,
        textClass: "ct_darksky_blue_text",
      },
    ],
    [visibleSubscriptions]
  );

  const closeModals = () => {
    setSelectedSubscription(null);
    setEditingSubscription(null);
    setEditForm(null);
    setValidationErrors({});
  };

  const openEditModal = (subscription) => {
    setEditingSubscription(subscription);
    setEditForm(getInitialEditState(subscription, adminCountryCurrencyList));
    setValidationErrors({});
  };

  const handleEditFieldChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    setValidationErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const handleFeatureChange = (index, value) => {
    setEditForm((prev) => ({
      ...prev,
      details: prev.details.map((detail, detailIndex) =>
        detailIndex === index ? { ...detail, feature_name: value } : detail
      ),
    }));
    setValidationErrors((prev) => ({
      ...prev,
      [`feature_${index}`]: "",
      details: "",
    }));
  };

  const addFeatureField = () => {
    setEditForm((prev) => ({
      ...prev,
      details: [...prev.details, { feature_name: "" }],
    }));
  };

  const removeFeatureField = (index) => {
    setEditForm((prev) => ({
      ...prev,
      details:
        prev.details.length === 1
          ? [{ feature_name: "" }]
          : prev.details.filter((_, detailIndex) => detailIndex !== index),
    }));
  };

  const handleCountryPriceChange = (index, value) => {
    setEditForm((prev) => ({
      ...prev,
      subscription_prices: prev.subscription_prices.map((item, itemIndex) =>
        itemIndex === index ? { ...item, price: value } : item
      ),
    }));
    setValidationErrors((prev) => ({
      ...prev,
      [`country_price_${index}`]: "",
      subscription_prices: "",
    }));
  };

  const handleCountryFreeToggle = (index, checked) => {
    setEditForm((prev) => ({
      ...prev,
      subscription_prices: prev.subscription_prices.map((item, itemIndex) =>
        itemIndex === index
          ? {
            ...item,
            is_free: checked,
            price: checked ? 0 : Number(item?.price) === 0 ? "" : item.price,
          }
          : item
      ),
    }));
    setValidationErrors((prev) => ({
      ...prev,
      [`country_price_${index}`]: "",
      subscription_prices: "",
    }));
  };

  const validateEditForm = () => {
    const errors = {};
    const title = String(editForm?.title || "").trim();
    const description = String(editForm?.description || "").trim();
    const yearsOfExperience = String(editForm?.years_of_experience || "").trim();
    const subscriptionType = String(editForm?.subscription_type || "").trim();
    const subscriptionDays = String(editForm?.subscription_days || "").trim();
    const isFree = !!editForm?.is_free;
    const numericPrice = Number(editForm?.price);
    const features = Array.isArray(editForm?.details) ? editForm.details : [];
    const countryPrices = Array.isArray(editForm?.subscription_prices)
      ? editForm.subscription_prices
      : [];

    if (!title) errors.title = "Title is required.";
    if (!description) errors.description = "Description is required.";
    if (!subscriptionType) errors.subscription_type = "Subscription type is required.";
    if (!subscriptionDays) errors.subscription_days = "Subscription duration is required.";
    if (!yearsOfExperience) {
      errors.years_of_experience = "Years of experience is required.";
    }

    if (!isFree) {
      if (!String(editForm?.price ?? "").trim()) {
        errors.price = "Price is required.";
      } else if (!Number.isFinite(numericPrice) || numericPrice <= 0) {
        errors.price = "Price must be greater than 0, or mark the plan as free.";
      }

      countryPrices.forEach((item, index) => {
        const itemPrice = Number(item?.price);
        if (item?.is_free) {
          return;
        }

        if (!String(item?.price ?? "").trim()) {
          errors[`country_price_${index}`] = "Country price is required.";
        } else if (!Number.isFinite(itemPrice) || itemPrice <= 0) {
          errors[`country_price_${index}`] =
            "Country price must be greater than 0.";
        }
      });
    }

    if (features.length === 0) {
      errors.details = "Please enter feature or field can not be empty.";
    }

    features.forEach((detail, index) => {
      if (!String(detail?.feature_name || "").trim()) {
        errors[`feature_${index}`] =
          "Please enter feature or field can not be empty.";
      }
    });

    return errors;
  };

  const handleUpdateSubscription = async (event) => {
    event.preventDefault();
    if (!editForm?.subscription_id || saving) return;

    const errors = validateEditForm();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);

    try {
      const formattedFeatures = editForm.details
        .map((detail) => String(detail?.feature_name || "").trim())
        .filter(Boolean);

      const payload = {
        price: editForm.is_free ? 0 : Number(editForm.price),
        title: String(editForm.title || "").trim(),
        description: String(editForm.description || "").trim(),
        subscription_days: Number(editForm.subscription_days || 0),
        subscription_type: String(editForm.subscription_type || "").trim(),
        years_of_experience: String(editForm.years_of_experience || "").trim(),
        save_percent: String(editForm.save_percent || "").trim(),
        features: formattedFeatures,
        subscription_prices: Array.isArray(editForm.subscription_prices)
          ? editForm.subscription_prices.map((item) => ({
            country_id: item?.country_id,
            price: item?.is_free ? 0 : Number(item?.price),
          }))
          : [],
      };

      const callback = (response, apiError) => {
        if (response?.success || response?.status) {
          loadSubscriptions();
          closeModals();
          return;
        }

        setError(
          apiError?.data?.message ||
          response?.message ||
          "Subscription update could not be completed. Please verify the endpoint payload."
        );
      };

      await dispatch(
        updateAdminSubscription({
          id: editForm.subscription_id,
          payload,
          callback,
        })
      );
    } catch (apiError) {
      setError(
        apiError?.data?.message ||
        "Subscription update could not be completed. Please verify the endpoint payload."
      );
    } finally {
      setSaving(false);
    }
  };

  if (isLoading && !adminSubscriptionList?.length) {
    return <Loader />;
  }

  return (
    <AdminLayout>
      <div className="ct_px_40 my-5">
        <div className="d-flex align-items-center justify-content-between gap-3 mb-4 ct_flex_col_767">
          <div>
            <h4 className="mb-1 ct_fs_20 ct_darkblue_text">Subscriptions</h4>
            <p className="mb-0 ct_grey_text">
              View and manage freelancer subscription plans from one place
            </p>
          </div>
        </div>

        {error ? (
          <div className="alert alert-warning mb-4" role="alert">
            {error}
          </div>
        ) : null}

        <div className="row">
          {summaryCards.map((card) => (
            <div className="col-lg-3 col-md-6 mb-4" key={card.label}>
              <div className="ct_admin_subscription_metric h-100">
                <p className="mb-2 ct_grey_text">{card.label}</p>
                <h3 className={`mb-0 ct_fw_700 ${card.textClass}`}>
                  {card.value}
                </h3>
              </div>
            </div>
          ))}
        </div>

        <div className="ct_admin_subscription_section mb-4">
          <div className="row">
            <div className="col-lg-4 mb-3">
              <label className="mb-2 ct_fs_14 ct_grey_text">Search</label>
              <input
                type="text"
                className="form-control ct_input"
                placeholder="Search by title, type, feature"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="col-lg-3 col-md-4 mb-3">
              <label className="mb-2 ct_fs_14 ct_grey_text">
                Experience Plan
              </label>
              <div className="position-relative">
                <select
                  className="form-control ct_input ct_admin_subscription_select"
                  value={planFilter}
                  onChange={(event) => setPlanFilter(event.target.value)}
                >
                  {PLAN_FILTERS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <i className="fa-solid fa-angle-down ct_admin_subscription_select_icon"></i>
              </div>
            </div>
            <div className="col-lg-5 col-md-4 mb-3">
              <label className="mb-2 ct_fs_14 ct_grey_text">Duration</label>
              <div className="position-relative">
                <select
                  className="form-control ct_input ct_admin_subscription_select"
                  value={durationFilter}
                  onChange={(event) => setDurationFilter(event.target.value)}
                >
                  {DURATION_FILTERS.map((option) => (
                    <option key={option} value={option}>
                      {option === "All" ? option : getDurationLabel(option)}
                    </option>
                  ))}
                </select>
                <i className="fa-solid fa-angle-down ct_admin_subscription_select_icon"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="table-responsive ct_custom_table ct_custom_table2 pt-2">
          <table className="table ct_admin_subscription_table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Plan Bucket</th>
                <th>Experience</th>
                <th>Type</th>
                <th>Duration</th>
                <th>Price</th>
                <th>Features</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscriptions.length > 0 ? (
                filteredSubscriptions.map((item) => {
                  const bucket = getExperienceBucket(item?.years_of_experience);

                  return (
                    <tr key={item?.subscription_id}>
                      <td>
                        <div className="ct_admin_subscription_title_cell">
                          <h5 className="ct_fs_16 mb-1 ct_darkblue_text">
                            {item?.title || "--"}
                          </h5>
                          <p className="mb-0 ct_grey_text ct_fs_14 ct_admin_subscription_desc">
                            {item?.description || "--"}
                          </p>
                        </div>
                      </td>
                      <td>
                        <span className={`ct_fw_600 ${getBucketTextClass(bucket)}`}>
                          {bucket}
                        </span>
                      </td>
                      <td>{item?.years_of_experience || "--"}</td>
                      <td className="ct_admin_subscription_type_cell">
                        {getSubscriptionTypeLabel(item?.subscription_type)}
                      </td>
                      <td>{getDurationLabel(item?.subscription_days)}</td>
                      <td>{formatPrice(item?.price)}</td>
                      <td>{item?.details?.length || 0}</td>
                      <td className="ct_admin_subscription_created_cell">
                        {formatDate(item?.created_at)}
                      </td>
                      <td className="ct_admin_subscription_actions_cell">
                        <div className="d-flex align-items-center gap-2 ct_admin_subscription_actions_wrap">
                          <button
                            type="button"
                            className="ct_yellow_btn ct_h_35 px-3"
                            onClick={() => setSelectedSubscription(item)}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            className="ct_yellow_btn ct_h_35 px-3 ct_outline_grey"
                            onClick={() => openEditModal(item)}
                          >
                            Update
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-4 ct_grey_text">
                    No subscriptions matched the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedSubscription ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header border-0 pb-0">
                  <h4 className="ct_fs_20 mb-0 ct_darkblue_text">
                    View Subscription
                  </h4>
                  <button
                    type="button"
                    className="btn-close ct_circle_close"
                    onClick={closeModals}
                  ></button>
                </div>
                <div className="modal-body p-4">
                  <div className="row align-items-start">
                    <div className="col-lg-7 mb-4 mb-lg-0">
                      <div
                        className={`ct_pricing_card p-3 ct_subscription_pricing_card d-flex flex-column ct_admin_subscription_view_card ${Number(selectedSubscription?.price ?? 0) === 0
                          ? "ct_subscription_free_card"
                          : ""
                          }`}
                      >
                        <div>
                          {selectedSubscription?.save_percent ? (
                            <span className="ct_subscription_badge ct_subscription_badge_best ct_admin_subscription_view_badge">
                              {selectedSubscription?.save_percent}
                            </span>
                          ) : null}
                          <div className="ct_pricing_title mt-3 text-center">
                            <p className="mb-2 ct_fs_24_new text-center ct_admin_subscription_view_title">
                              {selectedSubscription?.title || "--"}
                            </p>
                            <h2 className="ct_fs_24_new mb-0 text-center ct_fw_700 ct_subscription_price">
                              {formatPrice(selectedSubscription?.price)}
                            </h2>
                            <p className="mb-0 mt-3 ct_fw_600 ct_fs_16 ct_blue_text_1 text-center ct_admin_subscription_view_desc">
                              {selectedSubscription?.description || "--"}
                            </p>
                          </div>
                          <ul className="ct_mt_30 mt-4 ct_subscription_feature_list ct_admin_subscription_feature_list_view">
                            {(selectedSubscription?.details || []).length > 0 ? (
                              selectedSubscription.details.map((detail) => (
                                <li key={detail?.detail_id || detail?.feature_name}>
                                  <i
                                    className="fa-solid fa-check ct_subscription_check"
                                    style={{
                                      backgroundColor: getPlanAccentColor(
                                        selectedSubscription
                                      ),
                                    }}
                                  ></i>
                                  {detail?.feature_name || "--"}
                                </li>
                              ))
                            ) : (
                              <li>No feature details added yet.</li>
                            )}
                          </ul>
                        </div>
                        <div className="mt-auto pt-3 ct_subscription_card_actions">
                          <button
                            type="button"
                            className={`${getTitleButtonClass(
                              selectedSubscription
                            )} ct_subscription_btn`}
                            onClick={() => {
                              const current = selectedSubscription;
                              closeModals();
                              openEditModal(current);
                            }}
                          >
                            Update Details
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-5">
                      <div className="ct_admin_subscription_section h-100">
                        <div className="mb-3">
                          <p className="mb-1 ct_grey_text">Plan Bucket</p>
                          <h5
                            className={`mb-0 ${getBucketTextClass(
                              getExperienceBucket(
                                selectedSubscription?.years_of_experience
                              )
                            )}`}
                          >
                            {getExperienceBucket(
                              selectedSubscription?.years_of_experience
                            )}
                          </h5>
                        </div>
                        <div className="mb-3">
                          <p className="mb-1 ct_grey_text">Subscription Type</p>
                          <h6 className="mb-0 ct_darkblue_text">
                            {getSubscriptionTypeLabel(
                              selectedSubscription?.subscription_type
                            )}
                          </h6>
                        </div>
                        <div className="mb-3">
                          <p className="mb-1 ct_grey_text">Years Of Experience</p>
                          <h6 className="mb-0 ct_darkblue_text">
                            {selectedSubscription?.years_of_experience || "--"}
                          </h6>
                        </div>
                        <div className="mb-3">
                          <p className="mb-1 ct_grey_text">Duration</p>
                          <h6 className="mb-0 ct_darkblue_text">
                            {getDurationLabel(selectedSubscription?.subscription_days)}
                          </h6>
                        </div>
                        <div className="mb-0">
                          <p className="mb-1 ct_grey_text">Created On</p>
                          <h6 className="mb-0 ct_darkblue_text">
                            {formatDate(selectedSubscription?.created_at)}
                          </h6>
                        </div>
                        <div className="mt-4">
                          <p className="mb-2 ct_grey_text">Country Wise Prices</p>
                          <div className="ct_admin_subscription_currency_list">
                            {(selectedSubscription?.subscription_prices || []).length > 0 ? (
                              selectedSubscription.subscription_prices.map((item, index) => (
                                <div
                                  key={`${item?.country_id || index}-view-price`}
                                  className="ct_admin_subscription_currency_item"
                                >
                                  <span className="ct_admin_subscription_currency_name">
                                    {getCountryDisplayLabel(
                                      item?.country_id,
                                      item?.label,
                                      adminCountryCurrencyList
                                    ) || `Country ${index + 1}`}
                                  </span>
                                  <span className="ct_admin_subscription_currency_price">
                                    {Number(item?.price ?? 0) === 0
                                      ? "Free"
                                      : `${item?.price}`}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <p className="mb-0 ct_grey_text ct_fs_14">
                                No country prices added yet.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}

      {editingSubscription && editForm ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header border-0 pb-0">
                  <h4 className="ct_fs_20 mb-0 ct_darkblue_text">
                    Update Subscription
                  </h4>
                  <button
                    type="button"
                    className="btn-close ct_circle_close"
                    onClick={closeModals}
                  ></button>
                </div>
                <div className="modal-body p-4">
                  <form onSubmit={handleUpdateSubscription}>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="mb-2 ct_fs_14 ct_grey_text">Title</label>
                        <input
                          type="text"
                          className="form-control ct_input"
                          value={editForm.title}
                          onChange={(event) =>
                            handleEditFieldChange("title", event.target.value)
                          }
                        />
                        {validationErrors.title ? (
                          <p className="mb-0 mt-1 text-danger ct_fs_14">
                            {validationErrors.title}
                          </p>
                        ) : null}
                      </div>
                      <div className="col-md-6 mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <label className="mb-0 ct_fs_14 ct_grey_text">Price</label>
                          <div className="form-check d-flex align-items-center gap-2 mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={!!editForm.is_free}
                              id="is_free_plan"
                              onChange={(event) => {
                                const checked = event.target.checked;
                                setEditForm((prev) => ({
                                  ...prev,
                                  is_free: checked,
                                  price: checked ? 0 : prev.price,
                                }));
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  price: "",
                                }));
                              }}
                            />
                            <label htmlFor="is_free_plan" className="mb-0 ct_fs_14 ct_grey_text">
                              Mark as free plan
                            </label>
                          </div>
                        </div>
                        <input
                          type="number"
                          className="form-control ct_input"
                          value={editForm.price}
                          min="0"
                          disabled={!!editForm.is_free}
                          onChange={(event) =>
                            handleEditFieldChange("price", event.target.value)
                          }
                        />
                        {validationErrors.price ? (
                          <p className="mb-0 mt-1 text-danger ct_fs_14">
                            {validationErrors.price}
                          </p>
                        ) : null}
                      </div>
                      <div className="col-md-12 mb-3">
                        <label className="mb-2 ct_fs_14 ct_grey_text">
                          Description
                        </label>
                        <textarea
                          rows="3"
                          className="form-control ct_input ct_subscription_description_textarea"
                          value={editForm.description}
                          onChange={(event) =>
                            handleEditFieldChange(
                              "description",
                              event.target.value
                            )
                          }
                        ></textarea>
                        {validationErrors.description ? (
                          <p className="mb-0 mt-1 text-danger ct_fs_14">
                            {validationErrors.description}
                          </p>
                        ) : null}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="mb-2 ct_fs_14 ct_grey_text">
                          Save / Badge Text
                        </label>
                        <input
                          type="text"
                          className="form-control ct_input"
                          placeholder="Best Value, Save 10%, Most Popular"
                          value={editForm.save_percent}
                          onChange={(event) =>
                            handleEditFieldChange(
                              "save_percent",
                              event.target.value
                            )
                          }
                        />
                        {validationErrors.save_percent ? (
                          <p className="mb-0 mt-1 text-danger ct_fs_14">
                            {validationErrors.save_percent}
                          </p>
                        ) : null}
                      </div>
                      <div className="col-md-12 mb-2">
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                          <label className="mb-0 ct_fs_14 ct_grey_text">
                            Features
                          </label>
                          <button
                            type="button"
                            className="ct_yellow_btn ct_h_35 px-3"
                            onClick={addFeatureField}
                          >
                            Add Feature
                          </button>
                        </div>
                        {validationErrors.details ? (
                          <p className="mb-2 text-danger ct_fs_14">
                            {validationErrors.details}
                          </p>
                        ) : null}
                        {editForm.details.map((detail, index) => (
                          <div key={`feature-${index}`} className="mb-2">
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="text"
                                className="form-control ct_input"
                                value={detail.feature_name}
                                onChange={(event) =>
                                  handleFeatureChange(index, event.target.value)
                                }
                                placeholder={`Feature ${index + 1}`}
                              />
                              <button
                                type="button"
                                className="ct_yellow_btn ct_h_35 px-3 ct_outline_grey ct_subscription_feature_remove_btn"
                                onClick={() => removeFeatureField(index)}
                              >
                                Remove
                              </button>
                            </div>
                            {validationErrors[`feature_${index}`] ? (
                              <p className="mb-0 mt-1 text-danger ct_fs_14">
                                {validationErrors[`feature_${index}`]}
                              </p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                      <div className="col-md-12 mb-3 mt-2">
                        <label className="mb-2 ct_fs_14 ct_grey_text">
                          Country Wise Prices
                        </label>
                        <div className="row">
                          {(editForm.subscription_prices || []).map((item, index) => (
                            <div
                              className="col-md-6 mb-3"
                              key={`${item?.country_id}-${index}`}
                            >
                              <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                                <label className="mb-0 ct_fs_14 ct_grey_text">
                                  {getCountryDisplayLabel(
                                    item?.country_id,
                                    item?.label,
                                    adminCountryCurrencyList
                                  ) || `Country ${index + 1}`}
                                </label>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="ct_admin_currency_badge">
                                    {item?.value || "CUR"}
                                  </span>
                                  <div className="form-check d-flex align-items-center gap-2 mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={!!item?.is_free}
                                      id={`country_free_${item?.country_id}`}
                                      onChange={(event) =>
                                        handleCountryFreeToggle(
                                          index,
                                          event.target.checked
                                        )
                                      }
                                    />
                                    <label
                                      htmlFor={`country_free_${item?.country_id}`}
                                      className="mb-0 ct_fs_12 ct_grey_text"
                                    >
                                      Free
                                    </label>
                                  </div>
                                </div>
                              </div>
                              <input
                                type="number"
                                className="form-control ct_input"
                                min={item?.is_free ? "0" : "1"}
                                value={item?.is_free ? 0 : item?.price}
                                disabled={!!item?.is_free}
                                onChange={(event) =>
                                  handleCountryPriceChange(index, event.target.value)
                                }
                                placeholder={`Enter price in ${item?.value || ""}`}
                              />
                              {validationErrors[`country_price_${index}`] ? (
                                <p className="mb-0 mt-1 text-danger ct_fs_14">
                                  {validationErrors[`country_price_${index}`]}
                                </p>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-end gap-3 mt-4 ct_flex_col_575">
                      <button
                        type="button"
                        className="ct_yellow_btn ct_outline_grey ct_h_48 px-4"
                        onClick={closeModals}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="ct_yellow_btn ct_h_48 px-4"
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}
    </AdminLayout>
  );
};

export default Subscription;
